import { requestV2 } from '@ltvco/refresh-lib/utils';
import {
  CreateAnswerParams,
  CreateQuestionParams,
  GetQuestionParams,
  GetQuestionsParams,
  GetQuestionsResponse,
} from './interfaces';
import { normalizeYear } from 'utils/normalizeYear';
import {
  COMMUNITY_BUZZ_BASE_URL,
  DEFAULT_MAX_POLL_RETRIES,
  DEFAULT_POLL_RETRY_DELAY_MS,
} from './constants';

const normalizePage = (page?: number) => {
  if (page === null || page === undefined) return 1;
  if (page < 0) return 1;
  return page;
};

const normalizePageSize = (pageSize?: number) => {
  if (!pageSize || pageSize < 1) return String(20);
  if (pageSize > 100) return String(100);
  return String(pageSize);
};

const getCommunityBuzzHeaders = (token?: string) => ({
  'content-type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

class HttpError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.payload = payload;
  }
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (!signal) {
      setTimeout(resolve, ms);
      return;
    }

    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort);
  });

const createQuestion = async (params: CreateQuestionParams, token?: string) => {
  const normalizedYear = normalizeYear(params.year, 'community buzz');
  const payload = { ...params, year: normalizedYear };
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(payload),
  };

  return await requestV2({
    url: `${COMMUNITY_BUZZ_BASE_URL}/questions`,
    options: init,
  });
};

const createAnswer = async (params: CreateAnswerParams, token?: string) => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return await requestV2({
    url: `${COMMUNITY_BUZZ_BASE_URL}/question/${params.question_id}/answer`,
    options: init,
  });
};

const getQuestions = async (
  params: GetQuestionsParams,
  token?: string,
  maxRetries: number = DEFAULT_MAX_POLL_RETRIES,
  retryDelay: number = DEFAULT_POLL_RETRY_DELAY_MS,
  signal?: AbortSignal,
): Promise<GetQuestionsResponse> => {
  const normalizedYear = normalizeYear(params.year, 'community buzz');
  const searchParams = new URLSearchParams({
    year: normalizedYear,
    make: params.make,
    model: params.model,
    trim: params.trim,
  });

  if (params.page !== undefined && params.page !== null) {
    searchParams.set('page', normalizePage(params.page).toString());
  }
  if (params.page_size !== undefined && params.page_size !== null) {
    searchParams.set('page_size', normalizePageSize(params.page_size));
  }

  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'GET',
  };

  const url = `${COMMUNITY_BUZZ_BASE_URL}/questions?${searchParams.toString()}`;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const response = await fetch(url, { ...init, signal });
      const status = response.status;

      if (status === 201 || status === 202) {
        retryCount++;

        if (retryCount >= maxRetries) {
          throw new Error(
            `Polling exceeded maximum retries (${maxRetries}) for community buzz`,
          );
        }

        await sleep(retryDelay, signal);
        continue;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new HttpError(
          `Request failed with status ${status}: ${JSON.stringify(errorData)}`,
          status,
          errorData,
        );
      }

      const data = await response.json().catch(() => ({}));
      const safeResponse: GetQuestionsResponse = {
        items: Array.isArray(data.items) ? data.items : [],
        page: typeof data.page === 'number' ? data.page : 1,
        page_size: typeof data.page_size === 'number' ? data.page_size : 0,
        ...(typeof data.total === 'number' ? { total: data.total } : {}),
      };
      return safeResponse;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }

      if (signal?.aborted) {
        throw error;
      }

      retryCount++;
      if (retryCount >= maxRetries) {
        throw error;
      }
      await sleep(retryDelay, signal);
    }
  }

  throw new Error(
    `Polling exceeded maximum retries (${maxRetries}) for community buzz`,
  );
};

const getQuestion = async (params: GetQuestionParams, token?: string) => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'GET',
  };

  return await requestV2({
    url: `${COMMUNITY_BUZZ_BASE_URL}/question/${params.id}`,
    options: init,
  });
};

export const communityBuzzRequests = {
  createQuestion,
  createAnswer,
  getQuestions,
  getQuestion,
};
